import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "./Navigation";
import TransactionReceipt from "./TransactionReceipt";

// ✅ Use env if available; fallback to backend 3001
const API_ORIGIN =
  process.env.REACT_APP_API_BASE_URL ||
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE_URL) ||
  "http://localhost:3001";

const API_V1 = `${API_ORIGIN}/api/v2`;

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [repayModal, setRepayModal]   = useState(null);  // settlement object
  const [repayMethod, setRepayMethod] = useState('INTERAC');
  const [repayLoading, setRepayLoading] = useState(false);
  const [repayResult,  setRepayResult]  = useState(null);
  const navigate = useNavigate();
  const [showLoadFunds, setShowLoadFunds] = useState(false);

  useEffect(() => {
    // ✅ Accept either token key (legacy + new)
    const token =
      localStorage.getItem("zp_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken");

    if (!token) {
      setError("No token found. Please log in again.");
      setLoading(false);
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    async function safeJson(res) {
      try {
        return await res.json();
      } catch {
        return null;
      }
    }

    async function fetchData() {
      try {
        setLoading(true);
        setError("");

        // 1) ✅ User profile (REAL backend endpoint)
        const profileRes = await fetch(`${API_V1}/auth/profile`, { headers });
        const profileData = await safeJson(profileRes);
        if (!profileRes.ok) {
          const msg = profileData?.error || `Failed to load profile (${profileRes.status})`;
          throw new Error(msg);
        }
        setUser(profileData.data?.user || profileData.user || profileData);

        // 2) ✅ YC demo summary (REAL endpoint we just added)
        // This gives us profile + txCount even if wallet/tx endpoints aren’t ready.
        const summaryRes = await fetch(`${API_V1}/cockpit/user-summary`, { headers });
        const summaryData = await safeJson(summaryRes);
        if (summaryRes.ok) {
          setSummary(summaryData.summary || summaryData);
        } else {
          // don’t crash dashboard if summary isn’t available
          console.warn("Dashboard summary not available:", summaryData);
        }

        // 3) Wallet (optional — only if your backend actually has it)
        // If you don’t have this endpoint yet, we keep wallet as zeros.
        const walletFallback = { cad: 0, usd: 0, kes: 0 };
        try {
          const walletRes = await fetch(`${API_V1}/wallet/balance`, { headers });
          const walletData = await safeJson(walletRes);
          if (walletRes.ok && walletData?.balances) {
            // Map API response {balances: {CAD: x}} to {cad: x, usd: x, kes: x}
            setWallet({
              cad: walletData.balances.CAD || 0,
              usd: walletData.balances.USD || 0,
              kes: walletData.balances.KES || 0,
              wallets: walletData.wallets || [],
            });
          } else setWallet(walletFallback);
        } catch {
          setWallet(walletFallback);
        }

        // 4) Transactions (optional — depending on your backend route)
        // Your backend has /api/v2/transactions mounted; many projects use /api/v2/transactions
        try {
          const txRes = await fetch(`${API_V1}/transactions`, { headers });
          const txData = await safeJson(txRes);

          let txList = [];
          if (Array.isArray(txData)) txList = txData;
          else if (Array.isArray(txData?.transactions)) txList = txData.transactions;

          setTransactions(txList);
        } catch (e) {
          console.warn("Transactions not available yet:", e?.message);
          setTransactions([]);
        }

        setLoading(false);
      } catch (err) {
        console.error("❗ Error loading dashboard data:", err);
        setError(err.message || "Failed to load dashboard data.");
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const getDeliveryMethodLabel = (method) => {
    const methods = {
      MPESA: "M-Pesa",
      BANK: "Bank Transfer",
      AIRTIME: "Airtime",
    };
    return methods[method] || "Mobile Money";
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
  };

  if (loading) {

  return (
      <>
        <Navigation />
        <p className="p-6">Loading dashboard...</p>
      </>
    );
  }

  if (error) {

  return (
      <>
        <Navigation />
        <div className="p-6">
          <p className="text-red-500 mb-2">{error}</p>
          <p>Please try logging out and logging in again.</p>
        </div>
      </>
    );
  }

  // Wallet fallback if not loaded
  const w = wallet || { cad: 0, usd: 0, kes: 0 };

  // SNPL numbers from user if present
  const snplLimit = parseFloat(user?.snplLimit || 0);
  const snplAssessed = snplLimit > 0;
  const snplStatus = (user?.snplStatus || (snplLimit > 0 ? 'APPROVED' : 'UNASSESSED')).toUpperCase();
  const snplAvailable = (user?.snplAvailable != null) ? parseFloat(user.snplAvailable) : snplLimit;
  const snplUsed = parseFloat((user?.snplUsed != null) ? user.snplUsed : (snplLimit - snplAvailable));

  const displayName =
    user?.firstName ||
    user?.name?.split(" ")[0] ||
    user?.email?.split("@")[0] ||
    "User";



  const handleRepayEarly = async () => {
    if (!repayModal) return;
    setRepayLoading(true);
    setRepayResult(null);
    const token = localStorage.getItem("zp_token") || localStorage.getItem("token") || localStorage.getItem("accessToken");
    try {
      const res = await fetch(`${API_V1}/snpl/repay/${repayModal.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ method: repayMethod })
      });
      const d = await res.json();
      if (d.success) {
        setRepayResult({ ok: true, msg: d.message, credit: d.snplAvailable, creditEval: d.creditEvaluation });
        setTimeout(() => { setRepayModal(null); setRepayResult(null); window.location.reload(); }, 2000);
      } else {
        setRepayResult({ ok: false, msg: d.error || 'Repayment failed' });
      }
    } catch (e) {
      setRepayResult({ ok: false, msg: 'Network error: ' + e.message });
    }
    setRepayLoading(false);
  };

  return (
    <>
      <Navigation />
      <div className="p-6 space-y-6">
        <h1 className="text-3xl font-bold">Welcome, {displayName}!</h1>



        {/* Top cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border p-4 rounded shadow">
            <h2 className="font-bold text-lg mb-2">Wallet Balance</h2>
            <p>CAD: ${w.cad ?? 0}</p>
            <p>USD: ${w.usd ?? 0}</p>
            <p>KES: {w.kes ?? 0}</p>
            <button
              className="bg-blue-600 text-white p-2 w-full rounded mt-4"
              onClick={() => setShowLoadFunds(true)}
            >
              💰 Load Funds
            </button>

            {showLoadFunds && (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded mt-2 text-sm">
                <strong>Coming soon.</strong> You'll be able to load your wallet
                by Interac e-Transfer.
                <button
                  className="block mt-2 text-blue-700 underline"
                  onClick={() => setShowLoadFunds(false)}
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>

          <div className="border p-4 rounded shadow">
            <h2 className="font-bold text-lg mb-2">SNPL Credit</h2>
            {snplStatus === 'APPROVED' ? (
              <>
                <p>Limit: ${snplLimit.toFixed(2)}</p>
                <p>Used: ${parseFloat(snplUsed).toFixed(2)}</p>
                <p className="font-semibold text-green-700">Available: ${parseFloat(snplAvailable).toFixed(2)}</p>
              </>
            ) : snplStatus === 'PENDING_REVIEW' ? (
              <>
                <p className="text-sm font-semibold text-amber-700">Under Review</p>
                <p className="text-xs text-gray-500 mt-1">We're reviewing your affordability assessment. We'll notify you once it's complete.</p>
              </>
            ) : snplStatus === 'FROZEN' ? (
              <>
                <p className="text-sm font-semibold text-red-700">Temporarily Frozen</p>
                <p className="text-xs text-gray-500 mt-1">Please clear any outstanding settlement to restore your SNPL credit.</p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500">Available Credit</p>
                <p className="text-2xl font-bold">$0.00</p>
                <p className="text-xs text-gray-400 mb-3">(Not yet unlocked)</p>
                <div className="border-t pt-3">
                  <p className="text-sm font-semibold text-green-700 snpl-pulse">Unlock up to CAD $250</p>
                  <p className="text-xs text-gray-500 mt-1 mb-2">Connect your bank account to receive your personalized limit.</p>
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white text-sm font-semibold p-2 w-full rounded transition-colors"
                    onClick={() => navigate("/unlock-credit")}
                  >
                    Unlock My Limit
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="border p-4 rounded shadow">
            <h2 className="font-bold text-lg mb-2">Quick Actions</h2>

            <button
              className="bg-blue-600 text-white p-3 w-full rounded mb-2 font-semibold"
              onClick={() => navigate("/create-order")}
            >
              💸 Send Money
            </button>

            <button
              className="bg-green-600 text-white p-3 w-full rounded mb-2 font-semibold"
              onClick={() => navigate("/airtime")}
            >
              📱 Mobile Top-Up
            </button>

            <button
              className="bg-purple-600 text-white p-2 w-full rounded mb-2"
              onClick={() => navigate("/recipients")}
            >
              👥 Manage Recipients
            </button>

            <button
              className="border border-gray-300 text-gray-500 p-2 w-full rounded text-sm"
              onClick={() => navigate("/send")}
            >
              Legacy Send Money
            </button>

          </div>
        </div>

        <div className="border p-4 rounded shadow">
          <h2 className="font-bold text-lg mb-3">Recent Transactions</h2>

          {transactions.length === 0 ? (
            <p className="text-gray-500">No transactions yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-2">Date</th>
                    <th className="text-left py-3 px-2">Type</th>
                    <th className="text-left py-3 px-2">Delivery Method</th>
                    <th className="text-left py-3 px-2">Amount</th>
                    <th className="text-left py-3 px-2">Recipient</th>
                    <th className="text-left py-3 px-2">Payout Status</th>
                    <th className="text-left py-3 px-2">SNPL Status</th>
                    <th className="text-left py-3 px-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr
                      key={tx.id || tx._id || Math.random()}
                      className="border-b hover:bg-gray-50 cursor-pointer transition"
                      onClick={() => handleTransactionClick(tx)}
                    >
                      <td className="py-3 px-2">
                        {tx.createdAt
                          ? new Date(tx.createdAt).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </td>
                      <td className="py-3 px-2 font-semibold">
                        {tx.transactionType || tx.type || "SNPN"}
                      </td>
                      <td className="py-3 px-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {getDeliveryMethodLabel(tx.payoutMethod)}
                        </span>
                      </td>
                      <td className="py-3 px-2 font-semibold">
                        {tx.senderAmount
                          ? tx.transactionType === 'SNPL' && tx.snplSettlement?.totalAmount
                            ? `${parseFloat(tx.snplSettlement.totalAmount).toFixed(2)} ${tx.senderCurrency || "CAD"}`
                            : `${tx.senderAmount} ${tx.senderCurrency || "CAD"}`
                          : "-"}
                      </td>
                      <td className="py-3 px-2">
                        {tx.recipientName || tx.recipient || "-"}
                      </td>
                      <td className="py-3 px-2">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            (tx.payoutStatus || tx.status || "").toLowerCase() === "completed"
                              ? "bg-green-100 text-green-800"
                              : (tx.payoutStatus || tx.status || "").toLowerCase() === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {((tx.payoutStatus || tx.status || "pending").toString()).toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        {tx.transactionType === "SNPL" ? (
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              tx.snplSettlement?.status === "paid"
                                ? "bg-green-100 text-green-800"
                                : tx.snplSettlement?.status === "overdue"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {tx.snplSettlement?.status === "paid" ? "PAID" : tx.snplSettlement?.status === "overdue" ? "OVERDUE" : "PENDING"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            N/A
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleTransactionClick(tx); }}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            View Receipt →
                          </button>
                          {tx.transactionType === 'SNPL' &&
                           tx.snplSettlement &&
                           ['active','overdue','processing'].includes(tx.snplSettlement.status) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRepayMethod('INTERAC');
                                setRepayResult(null);
                                setRepayModal(tx.snplSettlement);
                              }}
                              className="text-emerald-600 hover:text-emerald-800 font-medium text-xs whitespace-nowrap"
                            >
                              💰 Repay Early
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedTransaction && (
        <TransactionReceipt
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}

      {/* ── Repay Early Modal ─────────────────────────────────────────── */}
      {repayModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">💰 Repay SNPL Early</h2>
              <button onClick={() => { setRepayModal(null); setRepayResult(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm">
              <p className="text-yellow-800 font-semibold">Amount Owing</p>
              <p className="text-2xl font-bold text-yellow-900">
                ${parseFloat(repayModal.remainingAmount || repayModal.totalAmount || 0).toFixed(2)} CAD
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                Due: {repayModal.dueDate ? (() => { const [y,m,d]=String(repayModal.dueDate).slice(0,10).split('-').map(Number); const M=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return (y&&m&&d)?`${M[m-1]} ${d}, ${y}`:'—'; })() : '—'}
              </p>
            </div>

            <p className="text-sm font-medium text-gray-700 mb-2">Payment Method</p>
            <div className="flex gap-3 mb-5">
              {['INTERAC', 'EFT'].map(m => (
                <button key={m}
                  onClick={() => setRepayMethod(m)}
                  className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-semibold transition ${
                    repayMethod === m
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                      : 'border-gray-200 text-gray-600 hover:border-emerald-300'
                  }`}>
                  {m === 'INTERAC' ? '🏦 Interac e-Transfer' : '🔄 EFT'}
                </button>
              ))}
            </div>

            {repayResult && (
              <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
                repayResult.ok ? 'bg-green-50 text-green-800 border border-green-200'
                               : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                <p>{repayResult.msg}</p>
                {repayResult.ok && repayResult.creditEval?.increased && (
                  <p className="mt-1 text-xs">🎉 Credit limit increased by $25 — 3 on-time payments!</p>
                )}
                {repayResult.ok && repayResult.creditEval?.evaluated && !repayResult.creditEval?.increased && (
                  <p className="mt-1 text-xs text-gray-600">{repayResult.creditEval.reason}</p>
                )}
              </div>
            )}

            <button
              onClick={handleRepayEarly}
              disabled={repayLoading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition"
            >
              {repayLoading ? 'Processing…' : `Confirm Repayment via ${repayMethod}`}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
